/**
 * RespaldoOdoo — qué asiento de Odoo respalda cada cosa del día.
 *
 * PARA QUIÉN. Operaciones, antes de mandar la factura a la aseguradora. Quien
 * la manda necesita ver que atrás hay asiento; contabilidad, después, verifica
 * consistencia. Hasta ahora eso obligaba a abrir Odoo y cruzar a mano.
 *
 * MUESTRA LO QUE ODOO DICE, NO LA MARCA GUARDADA. Es la regla R1 del spec
 * aplicada al respaldo: un verde apoyado en un booleano viejo miente.
 * `odoo_move_id` dice que algo se posteó alguna vez; el respaldo es que ese
 * asiento exista y esté posteado ahora. Entre las dos cosas cabe un asiento
 * pasado a borrador, uno borrado, y uno que nunca iba a existir — los tres se
 * vieron el mismo día.
 *
 * LO QUE NO VA A ODOO NO CUENTA COMO PENDIENTE (R3). Los fees de Yool y el
 * costo del PSP se facturan aparte: van en gris, fuera del conteo y plegados.
 * Contarlos manda a perseguir algo que nunca va a llegar.
 *
 * SÓLO LEE. No hay acciones en esta vista y no debe haberlas: lo que se mira
 * antes de firmar no puede tener efectos.
 *
 * @see src/services/yoolpay/respaldoApi.js
 * @see payment/src/services/respaldo-odoo.service.ts — los cinco veredictos
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { getRespaldoDelDia } from '../../../services/yoolpay/respaldoApi';
import { Aviso, Cifra, Encabezado, Fila, Rotulo, Seccion } from './cierre/piezas';
import { Badge } from './cierre/ds';
import { CIFRA } from '../../../lib/yoolpay/estados';

/**
 * Veredicto → cómo se rotula.
 *
 * ÁMBAR ES «HAY QUE MIRARLO», NO «ESTÁ ROTO» — la regla semántica de
 * `estados.js`. Un asiento en borrador o borrado es un estado legítimo mientras
 * se rehace un día; lo que no puede es pasar por respaldado. El rojo queda para
 * lo que no cuadra, y acá nada no cuadra: falta o no falta.
 */
const VEREDICTO = {
  respaldado: { variant: 'protegido', texto: 'Respaldado', color: CIFRA.verde },
  en_borrador: { variant: 'pendiente', texto: 'En borrador', color: CIFRA.ambar },
  no_esta: { variant: 'pendiente', texto: 'Sin asiento', color: CIFRA.ambar },
  // «NO SE PUDO PREGUNTAR» NO ES «NO ESTÁ». Celeste, que es el color de lo informativo
  // y de lo que espera: no hay nada que arreglar en la contabilidad, hay que reintentar.
  // Antes salía como «Sin asiento» y mandaba a rehacer un día que estaba sano.
  no_verificado: { variant: 'digital', texto: 'No verificado', color: CIFRA.celeste },
  sin_asiento: { variant: 'pendiente', texto: 'Sin asiento', color: CIFRA.ambar },
  no_aplica: { variant: 'neutro', texto: 'No aplica', color: CIFRA.apagado },
};

const COLS = '1.6fr 120px 1.4fr';

/** Un bloque —cobros o giros— con su encabezado y sus filas. */
function Bloque({ titulo, resumen }) {
  const [verNoAplica, setVerNoAplica] = useState(false);
  if (!resumen) return null;

  const accionables = resumen.items.filter((i) => i.veredicto !== 'no_aplica');
  const noAplica = resumen.items.filter((i) => i.veredicto === 'no_aplica');
  const visibles = verNoAplica ? [...accionables, ...noAplica] : accionables;

  return (
    <div style={{ marginTop: 18 }}>
      <div
        style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          padding: '0 24px 8px',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700 }}>{titulo}</span>
        <span style={{ fontSize: 12, color: 'var(--yool-muted)' }}>
          {/* SI NO SE PUDO VERIFICAR, NO SE DA UNA RAZÓN. «0 de 0 con asiento» sobre un
              día que no se pudo consultar suena a que no hay nada, y lo que pasa es que
              no se sabe. Los no verificados quedan fuera del denominador y se nombran. */}
          {(resumen.noVerificados ?? 0) > 0
            ? `${resumen.noVerificados} sin poder verificar`
            : `${resumen.respaldados} de ${resumen.respaldados + resumen.pendientes} con asiento`}
          {noAplica.length > 0 && (
            <>
              {' · '}
              <button
                type="button"
                onClick={() => setVerNoAplica((v) => !v)}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  font: 'inherit', color: 'var(--yool-muted)', textDecoration: 'underline',
                }}
              >
                {verNoAplica ? 'ocultar' : 'ver'} {noAplica.length} que se facturan aparte
              </button>
            </>
          )}
        </span>
      </div>

      {visibles.length === 0 ? (
        <div style={{ padding: '0 24px 4px' }}>
          <Aviso tono="baja" titulo="Sin movimiento.">
            Este día no registró {titulo.toLowerCase()}.
          </Aviso>
        </div>
      ) : (
        <>
          <Encabezado cols={COLS}>
            <Rotulo>Concepto</Rotulo>
            <Rotulo>Estado</Rotulo>
            <Rotulo>Documento en Odoo</Rotulo>
          </Encabezado>
          {visibles.map((i, n) => {
            const v = VEREDICTO[i.veredicto] ?? VEREDICTO.sin_asiento;
            return (
              <Fila key={`${i.concepto}-${n}`} cols={COLS} tenue={i.veredicto === 'no_aplica'}>
                {/* El id completo va en el `title`: es lo que se pega en un
                    ticket al proveedor, así que no se oculta del todo (R21). */}
                <span
                  title={i.concepto}
                  style={{
                    fontSize: 13, minWidth: 0, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {i.concepto}
                </span>
                <span>
                  <Badge variant={v.variant} soft ring>{v.texto}</Badge>
                </span>
                <Cifra align="left" size={12} weight={500} color={v.color}>
                  {i.detalle}
                </Cifra>
              </Fila>
            );
          })}
        </>
      )}
    </div>
  );
}

export default function RespaldoOdoo({ fecha, psp = 'virtualpos' }) {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);

  const load = useCallback(async () => {
    if (!fecha) return;
    setCargando(true);
    setError(null);
    // R5: al cambiar de día se limpia antes de mostrar. Nunca la tabla de ayer
    // bajo el encabezado de hoy.
    setData(null);
    try {
      setData(await getRespaldoDelDia(await getToken(), fecha, psp));
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [fecha, psp, getToken]);

  useEffect(() => { load(); }, [load]);

  const listo = data && !cargando;
  const completo = listo && data.cobros?.completo && data.giros?.completo;

  return (
    <Seccion
      titulo="Respaldo en Odoo"
      bajada="El asiento que respalda cada movimiento y cada giro del día, leído de Odoo."
      derecha={listo && !data.odooError ? (
        <Badge variant={completo ? 'protegido' : 'pendiente'} soft ring>
          {completo ? 'Con respaldo' : 'Falta respaldo'}
        </Badge>
      ) : null}
    >
      {cargando && (
        <div style={{ padding: '18px 24px', fontSize: 13, color: 'var(--yool-muted)' }}>
          Consultando Odoo…
        </div>
      )}

      {error && (
        <div style={{ padding: '18px 24px' }}>
          <Aviso tono="alta" titulo="No se pudo consultar el respaldo.">
            {error}
          </Aviso>
        </div>
      )}

      {/* UN PROBLEMA DE RED NO SE PINTA COMO UNO CONTABLE (R4). Sin esto, un
          Odoo caído se vería igual que un día entero sin asientos. */}
      {listo && data.odooError && (
        <div style={{ padding: '18px 24px 0' }}>
          <Aviso tono="alta" titulo="Odoo no respondió; los estados de abajo no son confiables.">
            {data.odooError}
          </Aviso>
        </div>
      )}

      {listo && !error && (
        <div style={{ paddingBottom: 8 }}>
          <Bloque titulo="Cobros" resumen={data.cobros} />
          <Bloque titulo="Giros" resumen={data.giros} />
        </div>
      )}
    </Seccion>
  );
}
